import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Toaster, toast } from "sonner";
import { apiForgotPassword, formatApiErrorDetail } from "../lib/api";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiForgotPassword(email.trim());
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-shell min-h-screen flex items-center justify-center px-6 py-12">
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: "rgba(18,18,23,0.95)",
            color: "#F5F5F5",
            border: "1px solid rgba(212,175,55,0.25)",
            backdropFilter: "blur(12px)",
            fontFamily: "Manrope, sans-serif",
          },
        }}
      />

      <div className="w-full max-w-md fade-up">
        <div className="text-center mb-10">
          <div
            className="w-12 h-12 rounded-full mx-auto flex items-center justify-center mb-4"
            style={{
              background: "linear-gradient(135deg, rgba(212,175,55,0.18), rgba(212,175,55,0.02))",
              border: "1px solid rgba(212,175,55,0.35)",
            }}
          >
            <span className="font-serif text-[#D4AF37] text-xl">M</span>
          </div>
          <p className="eyebrow">Forgot password</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#F5F5F5] mt-3 leading-tight">
            Find your <span className="italic text-[#D4AF37]">way back</span>
          </h1>
          <p className="text-[#8E8E93] mt-4 text-sm">
            Enter your email — we'll send a reset link.
          </p>
        </div>

        <div className="glass-card rounded-2xl p-8">
          {submitted ? (
            <div className="text-center space-y-3" data-testid="forgot-sent">
              <p className="eyebrow text-[#D4AF37]">Sent</p>
              <p className="font-serif text-2xl text-[#F5F5F5]">Check your inbox</p>
              <p className="text-[#8E8E93] text-sm">
                If an account exists for <span className="text-[#F5F5F5]">{email}</span>, a reset link has just been emailed to you. It expires in 1 hour.
              </p>
              <div className="gold-hairline w-16 mx-auto my-4" />
              <Link
                to="/login"
                className="text-[#D4AF37] hover:text-[#F3E5AB] transition-colors text-xs uppercase tracking-[0.2em]"
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="eyebrow text-[10px] block mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-luxe"
                  placeholder="you@example.com"
                  required
                  data-testid="forgot-email-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading || !email.trim()}
                className="btn-gold rounded-full w-full px-6 py-3 text-xs tracking-[0.25em] uppercase disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                data-testid="forgot-submit-btn"
              >
                {loading ? "Sending…" : "Send reset link"}
              </button>

              <div className="gold-hairline w-16 mx-auto my-2" />
              <p className="text-center text-sm text-[#8E8E93]">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#F3E5AB] transition-colors"
                >
                  <ArrowLeft size={13} /> Back to sign in
                </Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
